import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@breath-lab/state';
import { fetchSessionToken } from './sessionTokenCache';
import { useAccessGate } from '../ui/hooks/useAccessGate';
import { resolvePlayerConfig } from './logic';
import { breathworkPlayPath, upgradePath } from '../routes';
import type { Breathwork, BreathworkConfiguration } from './models';
import { dev } from '@breath-lab/domain';

const CONFIG_STORAGE_PREFIX = 'bl_config_v1_';

function loadStoredConfig(id: string): BreathworkConfiguration | null {
  try {
    const raw = localStorage.getItem(`${CONFIG_STORAGE_PREFIX}${id}`);
    return raw ? (JSON.parse(raw) as BreathworkConfiguration) : null;
  } catch {
    return null;
  }
}

/**
 * Hook that provides a quick-start handler for any breathwork.
 * Resolves config from localStorage/defaults, fetches session token for Pro,
 * and navigates directly to the player.
 */
export function useQuickStart(options?: { onTokenFailed?: () => void }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { ctx, isBreathworkAccessible } = useAccessGate();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const cancelledRef = useRef(false);
  const onTokenFailedRef = useRef(options?.onTokenFailed);
  useEffect(() => { onTokenFailedRef.current = options?.onTokenFailed; }, [options?.onTokenFailed]);

  const quickStart = useCallback(
    (breathwork: Breathwork, options?: { force?: boolean }) => {
      if (!breathwork.configurationUi) return;

      if (!options?.force && !isBreathworkAccessible(breathwork.id)) {
        navigate(upgradePath(), { state: { returnTo: window.location.pathname } });
        return;
      }

      const savedConfig = loadStoredConfig(breathwork.id);
      const config = resolvePlayerConfig(
        breathwork.id,
        breathwork.configurationUi,
        user?.settings ?? null,
        savedConfig,
      );

      const isPremium = !ctx.freeBreathworkIds.includes(breathwork.id);
      dev.log('[quick-start] breathwork:', breathwork.id, '| isPremium:', isPremium);

      const returnTo = window.location.pathname;

      if (!isPremium) {
        dev.log('[quick-start] free content, navigating to player directly');
        navigate(breathworkPlayPath(breathwork.id), {
          state: { config, sessionToken: null, returnTo },
        });
        return;
      }

      dev.log('[quick-start] premium content, fetching session token');
      cancelledRef.current = false;
      setLoadingId(breathwork.id);
      fetchSessionToken({ breathworkId: breathwork.id })
        .then((token) => {
          if (!cancelledRef.current) {
            dev.log('[quick-start] token acquired, navigating to player');
            navigate(breathworkPlayPath(breathwork.id), {
              state: { config, sessionToken: token, returnTo },
            });
          }
        })
        .catch((e) => {
          if (!cancelledRef.current) {
            dev.error('[quick-start] token fetch failed', e);
            if (onTokenFailedRef.current) {
              onTokenFailedRef.current();
            } else {
              navigate(upgradePath(), { state: { returnTo: window.location.pathname } });
            }
          }
        })
        .finally(() => {
          if (!cancelledRef.current) setLoadingId(null);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, user, ctx, isBreathworkAccessible],
  );

  return { quickStart, loadingId };
}
